import { MapPin } from 'lucide-react'

import { Playlist } from '@/core/api/entities/playlist/playlist'
import { Song } from '@/core/api/entities/song/song'
import useTranslation from '@/core/i18n/useTranslation'
import { usePlaylistTracking } from '@/shared/analytics/usePlaylistTracking'
import { Button } from '@/shared/components/atoms/Button'
import Favorite from '@/shared/components/atoms/Favorite'
import Icon from '@/shared/components/atoms/Icon'
import Image from '@/shared/components/atoms/Image'
import { cn } from '@/shared/utils/helpers'

interface PlaylistHeaderProps {
	playlist: Playlist
	filteredSongs: Song[]
	updatedDate: string
	isFavorite: boolean
	isFavoriting: boolean
	handleFavoriteClick: () => void
	onPlayNow: () => void
	// Mobile action props
	canRequestModifications?: boolean
	onRequestModificationsClick?: () => void
	onRateClick?: () => void
	canAssignPlaylists?: boolean
	onManageAssignmentClick?: () => void
}

// Utility function to format seconds to HH:MM
const formatHours = (seconds: number, t: (key: string) => string): string => {
	const totalMinutes = Math.floor(seconds / 60)
	const hours = Math.floor(totalMinutes / 60)
	const minutes = totalMinutes % 60
	return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${t('entities.music.hours')}`
}

export const PlaylistHeader = ({
	playlist,
	filteredSongs,
	updatedDate,
	isFavorite,
	isFavoriting,
	handleFavoriteClick,
	onPlayNow,
	canRequestModifications,
	onRequestModificationsClick,
	onRateClick,
	canAssignPlaylists,
	onManageAssignmentClick,
}: PlaylistHeaderProps) => {
	const { t, currentLanguage, getTranslation } = useTranslation('common')
	const { trackPlayNow } = usePlaylistTracking()

	const handlePlayNowClick = () => {
		if (filteredSongs.length > 0) {
			const firstSong = filteredSongs[0]
			trackPlayNow(
				{
					playlistId: playlist.id,
					playlistTitle: playlist.title,
				},
				{
					songId: firstSong.id,
					songTitle: firstSong.metadata.title,
					artistName: firstSong.metadata.artist,
				}
			)
		}
		onPlayNow()
	}

	const title = getTranslation(
		playlist?.titleTranslations || {},
		currentLanguage,
		playlist?.title || ''
	)
	const description = getTranslation(
		playlist?.descriptionTranslations || {},
		currentLanguage,
		playlist?.description || ''
	)
	// Calculate total duration by summing all song lengths
	const songsCount = playlist.analytics?.count || playlist.songs?.length || 0
	// Use playlist.songs if available (all songs), otherwise use filteredSongs
	const songsToCalculate =
		playlist.songs && playlist.songs.length > 0 ? playlist.songs : filteredSongs
	const totalDuration = songsToCalculate.reduce((total: number, song: Song) => {
		return total + (song.metadata.length || 0)
	}, 0)

	return (
		<div className="mb-8">
			<div className="flex gap-8 items-stretch max-md:flex-col max-md:items-start">
				<div className="relative w-[260px] h-[260px] md:w-[200px] md:h-[200px] shrink-0 rounded-3xl overflow-hidden border border-border/10 dark:border-white/10 m-auto lg:m-0">
					<Image
						src={playlist.cover || '/default-playlist.png'}
						alt={playlist.title}
						width="100%"
						height="100%"
					/>
					<div className="absolute inset-0 flex items-start justify-end p-2 pointer-events-none">
						<div className="bg-black/50 rounded-full w-8 h-8 flex items-center justify-center backdrop-blur-[4px] pointer-events-auto">
							<Favorite
								filled={isFavorite}
								onClick={handleFavoriteClick}
								size="small"
								filledColor="white"
								outlinedColor="white"
								isLoading={isFavoriting}
								disabled={isFavoriting}
							/>
						</div>
					</div>
				</div>

				<div
					className={cn(
						'flex-1 flex flex-col gap-4 w-full',
						!description && 'justify-between'
					)}
				>
					<div className="flex flex-col gap-4">
						<h1 className="block md:hidden text-xl font-bold text-foreground m-0 leading-[1.2]">
							{title}
						</h1>
						{/* only mobile */}
						<div className="flex md:hidden flex-wrap gap-1 mb-4">
							<div className="flex items-center gap-1 px-2 py-1 bg-accent rounded-md">
								<Icon
									name="music-note-beamed"
									width="16px"
									height="16px"
									color="var(--foreground)"
								/>
								<span className="text-xs text-foreground">
									{songsCount} {t('entities.music.songs')}
								</span>
							</div>
							<div className="flex items-center gap-1 px-2 py-1 bg-accent rounded-md">
								<Icon
									name="clock-fill"
									width="16px"
									height="16px"
									color="var(--foreground)"
								/>
								<span className="text-xs text-foreground">
									{formatHours(totalDuration, t)}
								</span>
							</div>
							<div className="flex items-center gap-1 px-2 py-1 bg-accent rounded-md">
								<Icon
									name="arrow-repeat"
									width="16px"
									height="16px"
									color="var(--foreground)"
								/>
								<span className="text-xs text-foreground">{updatedDate}</span>
							</div>
						</div>
						<div className="w-full flex gap-2 flex-wrap items-center">
							{/* Desktop: keep primary Play Now visible */}
							<Button
								variant="default"
								className="rounded-full w-fit hidden md:inline-flex"
								onClick={handlePlayNowClick}
								disabled={filteredSongs.length === 0}
							>
								<Icon
									name="play-sm"
									width="16px"
									height="16px"
									color="currentColor"
								/>
								{t('entities.music.play_now')}
							</Button>

							{/* Mobile: 4 action buttons in Figma order: Calificar, Modificar, Like, Play */}
							<div className="flex w-full items-center justify-between md:hidden gap-2 flex-wrap">
								<div className="flex gap-2">
									{canAssignPlaylists && (
										<Button
											variant="outline"
											size="action"
											className="bg-shade border border-foreground/10 dark:border-white/10 rounded-full hover:bg-black/5 dark:hover:bg-white/10"
											onClick={onManageAssignmentClick}
										>
											{t('entities.playlist.manage_assignment')}
											<MapPin className="!h-4 !w-4" />
										</Button>
									)}
									<Button
										variant="outline"
										size="action"
										className="bg-shade border border-foreground/10 dark:border-white/10 rounded-full hover:bg-black/5 dark:hover:bg-white/10"
										onClick={onRateClick}
									>
										{t('entities.music.rate')}
										<Icon
											name="star"
											width="16px"
											height="16px"
											color="var(--foreground)"
										/>
									</Button>
									{canRequestModifications &&
										!playlist.public &&
										onRequestModificationsClick && (
											<Button
												type="button"
												variant="outline"
												size="action"
												onClick={onRequestModificationsClick}
												className="bg-shade border border-foreground/10 dark:border-white/10 rounded-full hover:bg-black/5 dark:hover:bg-white/10"
											>
												{t('entities.music.modify')}
												<Icon
													name="pencil"
													width="16px"
													height="16px"
													color="currentColor"
												/>
											</Button>
										)}
									{/* <Button
										variant="outline"
										size="icon"
										className="rounded-full bg-shade border border-foreground/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10"
										onClick={handleFavoriteClick}
										disabled={isFavoriting}
										aria-label={t('entities.music.like')}
									>
										<span className="sr-only">{t('entities.music.like')}</span>
										<Icon
											name={isFavorite ? 'heart-fill' : 'heart'}
											width="16px"
											height="16px"
											color={
												isFavorite ? 'var(--primary)' : 'var(--foreground)'
											}
										/>
									</Button> */}
								</div>

								<Button
									variant="default"
									size="icon"
									className="rounded-full"
									onClick={handlePlayNowClick}
									disabled={filteredSongs.length === 0}
									aria-label={t('entities.music.play_now')}
								>
									<span className="sr-only">
										{t('entities.music.play_now')}
									</span>
									<Icon
										name="play-sm"
										width="16px"
										height="16px"
										color="currentColor"
									/>
								</Button>
							</div>
						</div>
					</div>
					{description && (
						<p className="text-base text-muted-foreground m-0 leading-[1.6]">
							{description}
						</p>
					)}

					{/* only desktop */}
					<div className="hidden md:flex flex-wrap gap-4">
						<div className="flex items-center gap-1 px-2 py-1 bg-transparent rounded-md border-2 border-border/25 dark:border-white/25">
							<Icon
								name="music-note-beamed"
								width="16px"
								height="16px"
								color="var(--foreground)"
							/>
							<span className="text-xs text-foreground">
								{songsCount} {t('entities.music.songs')}
							</span>
						</div>
						<div className="flex items-center gap-1 px-2 py-1 bg-transparent rounded-md border-2 border-border/25 dark:border-white/25">
							<Icon
								name="clock-fill"
								width="16px"
								height="16px"
								color="var(--foreground)"
							/>
							<span className="text-xs text-foreground">
								{formatHours(totalDuration, t)}
							</span>
						</div>
						<div className="flex items-center gap-1 px-2 py-1 bg-transparent rounded-md border-2 border-border/25 dark:border-white/25">
							<Icon
								name="arrow-repeat"
								width="16px"
								height="16px"
								color="var(--foreground)"
							/>
							<span className="text-xs text-foreground">
								{t('entities.music.updated_on')} {updatedDate}
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}
